'use client';

import { useRef, type CSSProperties } from 'react';
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useReducedMotion,
  type MotionValue,
} from 'motion/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import posthog from 'posthog-js';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { signUpUrl } from '@/lib/links';
import { Zap, CheckCircle2 } from 'lucide-react';
import { HiSparkles } from 'react-icons/hi2';
import { TypographyH1 } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

/**
 * Illustration-led home hero: copy up top on the warm maze stage, the combined
 * man + desert scene anchored to the bottom, and a curved seam that carries the
 * cream into the next section. Copy fades up on first view (replays on re-entry).
 * Sanctioned design-system departure (cream stage) — see apps/web/CLAUDE.md.
 */

const EASE = [0.22, 1, 0.36, 1] as const;

/** Decorative star that pops in then twinkles (CSS). */
function Star({
  className,
  show,
  delay = 0,
  style,
  y,
}: {
  className?: string;
  show: boolean;
  delay?: number;
  style?: CSSProperties;
  y?: MotionValue<number>;
}) {
  return (
    <motion.div
      aria-hidden
      style={{ y }}
      initial={{ opacity: 0, scale: 0 }}
      animate={show ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
      transition={{ type: 'spring', stiffness: 340, damping: 13, delay: show ? delay : 0 }}
      className={cn('pointer-events-none absolute z-10 select-none', className)}
    >
      <div className="animate-twinkle" style={style}>
        <Image
          src="/deco-star.png"
          alt=""
          width={40}
          height={40}
          draggable={false}
          className="h-auto w-full"
        />
      </div>
    </motion.div>
  );
}

export function Hero() {
  const t = useTranslations('hero');
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { amount: 0.2 });
  const reduce = useReducedMotion();

  // Parallax: as the hero scrolls away, the copy and stars drift up faster than
  // the desert scene (which stays put so it keeps tracking the curve) — the speed
  // difference reads as depth. Transform-only; collapses under reduced motion.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const copyY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
  const starAY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -170]);
  const starBY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -120]);

  const copy = (i: number) => ({
    initial: { opacity: 0, y: 22 },
    animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    transition: { duration: 0.6, ease: EASE, delay: i * 0.08 },
  });

  return (
    <section
      ref={sectionRef}
      className="app-bg-maze relative overflow-hidden bg-hero-peach pt-32 sm:pb-0 xl:min-h-[calc(600px_+_41vw)] xl:pt-0"
    >
      {/* ── Copy — centered; in flow below xl, pinned to the top on xl ── */}
      <motion.div
        style={{ y: copyY }}
        className="container-brand relative z-40 mx-auto flex max-w-3xl flex-col items-center text-center xl:absolute xl:inset-x-0 xl:top-[96px]"
      >
        <motion.div {...copy(0)}>
          <Badge
            variant="ghost"
            className="bg-sand px-3.5 py-1.5 text-caption font-medium tracking-normal text-primary"
          >
            <HiSparkles aria-hidden className="h-3.5 w-3.5 text-brand-gold" />
            <span>{t('badge')}</span>
          </Badge>
        </motion.div>

        <motion.div {...copy(1)}>
          <TypographyH1 className="mx-auto mt-5 max-w-3xl font-display text-[clamp(2.25rem,5vw,4.25rem)] leading-[1.08] tracking-[-0.015em] text-primary">
            {t('headline')}
          </TypographyH1>
        </motion.div>

        <motion.p
          {...copy(2)}
          className="mx-auto mt-5 max-w-xl text-body text-primary/80 sm:text-body-lg"
        >
          {t('subheadline')}
        </motion.p>

        <motion.div {...copy(3)} className="mt-6 flex w-full justify-center">
          <Button asChild size="lg" variant="default" className="group h-12 w-full px-8 sm:w-auto">
            <a
              href={signUpUrl}
              onClick={() => posthog.capture('signup_cta_clicked', { location: 'hero' })}
            >
              <Zap className="h-5 w-5 fill-current" />
              {t('primaryCta')}
            </a>
          </Button>
        </motion.div>

        <motion.div
          {...copy(4)}
          className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-caption text-muted-foreground"
        >
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 fill-brand-terracotta text-destructive-foreground" />
            {t('availability')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 fill-brand-terracotta text-destructive-foreground" />
            {t('setupTime')}
          </span>
        </motion.div>
      </motion.div>

      {/* ── Two twinkling stars in the clear sky (parallax at different rates) ── */}
      <Star show={inView} delay={0.5} y={starAY} className="start-[10%] top-[18%] w-4 sm:w-5" />
      <Star
        show={inView}
        delay={0.75}
        y={starBY}
        className="end-[11%] top-[24%] w-4 sm:w-5"
        style={{ animationDelay: '1.2s' }}
      />

      {/* ── Mobile (below sm): the carpet scene cropped tight to the seated
            figure — the full wide scene only reads sm+. Tune scale + object
            position to frame just the person. ── */}
      <div
        className="relative z-[5] mt-2 block h-[84vw] w-full overflow-hidden sm:hidden"
        aria-hidden
      >
        <Image
          src="/hero-carpet-v2.webp"
          alt=""
          fill
          priority
          fetchPriority="high"
          // Hidden via `sm:hidden` above the sm breakpoint — telling Next it's
          // 0-width there (not 100vw) matches the real rendered size and
          // silences the "not rendered at full viewport width" warning.
          sizes="(min-width: 640px) 0px, 100vw"
          // object-cover here is height-fit (box is always narrower than the art),
          // so the vertical crop is set purely by this zoom: 1.42 clipped the
          // figure's shoes (~87% down the art) at ~85%. 1.2 keeps the whole
          // seated figure — head to shoes — inside the frame.
          className="scale-[1.2] select-none object-cover object-center"
        />
      </div>

      {/* ── Combined scene (man + desert) — sits above the curve divider, in flow
            below xl, absolute along the bottom on xl. ── */}
      <div className="relative z-[5] -mt-[30vw] hidden w-full sm:block xl:absolute xl:inset-x-0 xl:bottom-0 xl:mt-0">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
        >
          <Image
            src="/hero-carpet-v2.webp"
            alt={t('figureAlt')}
            width={2400}
            height={1798}
            priority
            fetchPriority="high"
            sizes="100vw"
            className="h-auto w-full select-none"
          />
        </motion.div>
      </div>

      {/* ── Bottom seam — full-width dip the scene sits on; the cream fill carries
            the hero into the next section. ── */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3]" aria-hidden>
        <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="block h-[30vw] w-full">
          <path
            d="M0 200 L0 1.8 L72 27.3 L144 41.3 L216 51.2 L288 59.8 L360 66 L432 71.3 L504 75 L576 78.3 L648 82 L720 80.9 L792 79.8 L864 78.7 L936 75.8 L1008 71.7 L1080 67.6 L1152 62.3 L1224 55.3 L1296 45.8 L1368 31 L1440 9.2 L1440 200 Z"
            fill="var(--background)"
          />
          <path
            d="M0 1.8 L72 27.3 L144 41.3 L216 51.2 L288 59.8 L360 66 L432 71.3 L504 75 L576 78.3 L648 82 L720 80.9 L792 79.8 L864 78.7 L936 75.8 L1008 71.7 L1080 67.6 L1152 62.3 L1224 55.3 L1296 45.8 L1368 31 L1440 9.2"
            fill="none"
            stroke="#ffffff"
            strokeWidth={16}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    </section>
  );
}
